import { Card, Col, Modal, Row, Table } from 'antd';
import { Filter } from 'iconsax-react';
import isNull from 'lodash/isNull';
import { useEffect, useState } from 'react';
import { PiDownload, PiUpload } from 'react-icons/pi';
import { RiAddLine, RiCloseFill } from 'react-icons/ri';
import { useIntl } from 'react-intl';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { ActionButton, BreadCrumbs, IntlMessages, PrivateComponent } from '@/components';
import appRoutes from '@/configs/routes';
import hasActiveFilter from '@/lib/hasActiveFilter';
import useDownload from '../../../../../app/hooks/useDownload';
import useNavigationSource from '../../../../../app/hooks/useNavigationSource';
import useQueryParams from '../../../../../app/hooks/useQueryParams';
import useToggle from '../../../../../app/hooks/useToggle';
import DocumentationTooltip from '../../../../../components/DocumentationTooltip/DocumentationTooltip';
import Sidebar from '../../../../../components/Sidebar';
import basePagination from '../../../../../const/pagination';
import { Permissions } from '../../../../../const/permissions';
import applyFilter from '../../../../../lib/applyFilter';
import getQueryParams from '../../../../../lib/getQueryParams';
import handleResponse from '../../../../../lib/handleResponse';
import makeTableLoadingObject from '../../../../../lib/makeTableLoadingObject';
import parseQueryParam from '../../../../../lib/parseQueryParam';
import resetFilter from '../../../../../lib/resetFilter';
import getFeatureSourceTranslation from '../../../../Featured/utils/getFeatureSourceTranslation';
import {
  useDeleteDictionaryMutation,
  useGetDictionariesFilterQuery,
  useGetDictionariesQuery,
  useUpdateDictionaryMutation,
} from '../../../api/dictionariesApiSlice';
import { useGetLanguagesQuery } from '../../../api/languagesApiSlice';
import PreviewDictionaryTranslation from '../../../components/PreviewDictionaryTranslation/PreviewDictionaryTranslation';
import FormFilterDictionary from '../../../forms/FormFilterDictionary';
import FormGenerateTemplate from '../../../forms/FormGenerateTemplate';
import { getDictionariesTableColumns } from './PageDictionaries.const';

const PageDictionaries = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const [filterOpen, toggleFilterSidebar] = useToggle();
  const [searchQueryParams, setSearchParams] = useSearchParams();
  const [openModal, toggleModal] = useToggle();
  const [openExportTemplateModal, toggleExportTemplateModal] = useToggle();
  const [previewDictionary, setPreviewDictionary] = useState({});
  const [initFilterValues, setInitFilterValues] = useState({});
  const { urlPrefix, navigationSource } = useNavigationSource();

  const multipleSources = searchQueryParams.get('multipleSources') ?? null;
  const searchFilterFields = [
    'name',
    'source',
    'field',
    'field_value',
    'version',
    'created_from',
    'created_to',
  ];

  const { pagination, searchParams, handleChangeTableParams } = useQueryParams({ searchFilterFields });

  const { download: downloadTemplate, isLoading: isDownloadingTemplate } = useDownload({ method: 'POST' });

  let searchParamsData = searchParams.toString();

  if (multipleSources && !searchParams.get('search')) {
    searchParams.set('search', `source:${multipleSources}`);
    searchParamsData = searchParams.toString();
  }
  const {
    data: { data: dictionaries = [], pagination: dataPagination = {} } = { data: [], pagination: {} },
    isFetching: isLoading,
  } = useGetDictionariesQuery(
    {
      queryParams: `${searchParamsData}&searchJoin=and&include=structure,translations,removable`,
    },
    { skip: !searchParamsData.includes('limit') },
  );

  const { data: filterData = { fields: [], sources: [], versions: [] } } = useGetDictionariesFilterQuery({
    queryParams: navigationSource && !multipleSources ? `source=${navigationSource}` : '',
  });

  const { data: { data: languages = [] } = { data: [] } } = useGetLanguagesQuery({ queryParams: 'limit=0' });

  const [deleteDictionary, { isLoading: isLoadingDeleteDictionary }] = useDeleteDictionaryMutation();
  const [updateDictionary, { isLoading: isLoadingUpdateDictionary }] = useUpdateDictionaryMutation();

  const filteredSources = multipleSources
    ? {
        fields: filterData.fields,
        versions: filterData.versions,
        sources: filterData.sources.filter((source) => multipleSources.includes(source.label)),
      }
    : filterData;

  useEffect(() => {
    const queryParams = getQueryParams(window.location.search);
    const initValues = {};

    if (queryParams.search) {
      queryParams.search.split(';').forEach((item) => {
        const [key, value] = item.split(':');

        if (key === 'created_to') {
          initValues[key] = value.substring(0, 10);
        } else if (key === 'source' && multipleSources) {
          initValues[key] = multipleSources.split(',').map((item) => item.toLowerCase());
        } else if (key === 'source') {
          initValues[key] = parseQueryParam(value);
        } else {
          initValues[key] = value;
        }
      });
    }

    if (!navigationSource && 'source' in initValues) {
      initValues.source = null;
    }

    setInitFilterValues(initValues);
  }, [navigationSource]);

  const handleDelete = (id) => {
    deleteDictionary(id)
      .unwrap()
      .then(() => handleResponse('success', intl, 'ui-general-deleted-successfully'))
      .catch((error) => handleResponse('error', intl, 'ui-general-delete-failed', [], error));
  };

  const handleView = (dictionary) => {
    setPreviewDictionary(dictionary);
    toggleModal();
  };

  const handleChangeStatus = (id, values) => {
    updateDictionary({ dictionaryId: id, values });
  };

  const { download } = useDownload();

  const onDownload = async (dictionary_id) => {
    download(`dictionaries/${dictionary_id}/exports`);
  };

  const onDownloadTemplate = (values) => {
    downloadTemplate('dictionaries/templates/exports', values);
    toggleExportTemplateModal();
  };

  return (
    <>
      <Modal
        title={<IntlMessages id="dictionaries-translation-preview" />}
        width="60%"
        centered
        destroyOnHidden
        open={openModal}
        onCancel={toggleModal}
        footer={null}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <PreviewDictionaryTranslation dictionaryDetails={previewDictionary} languages={languages} />
      </Modal>

      <Modal
        title={<IntlMessages id="dictionaries-export-template" />}
        width={700}
        centered
        destroyOnHidden
        open={openExportTemplateModal}
        onCancel={toggleExportTemplateModal}
        footer={null}
        closeIcon={<RiCloseFill className="remix-icon text-color-black-100" size={24} />}
      >
        <FormGenerateTemplate onSubmit={onDownloadTemplate} isSubmitting={isDownloadingTemplate} />
      </Modal>

      <Row gutter={[32, 32]} justify="space-between" className="hp-mb-12" align="middle">
        <BreadCrumbs
          breadCrumbParent={
            urlPrefix ? (
              <Link to={appRoutes.urlPageFeatures({ featureSource: urlPrefix })}>
                <IntlMessages id={getFeatureSourceTranslation(urlPrefix)} />
              </Link>
            ) : null
          }
          breadCrumbActive={
            <div>
              <IntlMessages id="dictionaries-breadcrumbs" />
              <DocumentationTooltip link="https://x-flow-ltd.atlassian.net/wiki/spaces/HCD/pages/479592588/Dictionaries" />
            </div>
          }
        />

        <Col>
          <PrivateComponent allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.FILES.IMPORT]}>
            <ActionButton
              title={<IntlMessages id="ui-general-import" />}
              icon={<PiDownload data-testid="export-icon" />}
              onClick={() =>
                navigate(
                  appRoutes.urlPageDictionariesImport({
                    urlPrefix,
                    source: navigationSource ?? null,
                    multipleSources,
                  }),
                )
              }
            />
          </PrivateComponent>
          <PrivateComponent
            allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.FILES.EXPORT_TEMPLATE]}
          >
            <ActionButton
              title={<IntlMessages id="dictionaries-export-template" />}
              icon={<PiUpload />}
              onClick={toggleExportTemplateModal}
            />
          </PrivateComponent>
          <PrivateComponent allowedPermissions={[Permissions.TRANSLATIONS.DICTIONARIES.LIST.CREATE]}>
            <ActionButton
              title={<IntlMessages id="ui-general-create" />}
              icon={<RiAddLine className="btn-icon-mr-1" />}
              onClick={() =>
                navigate(
                  appRoutes.urlPageDictionaryCreate({
                    urlPrefix,
                    source: navigationSource ?? null,
                    multipleSources,
                  }),
                )
              }
            />
          </PrivateComponent>

          <ActionButton
            isActive={hasActiveFilter(searchQueryParams, searchFilterFields)}
            icon={<Filter size={18} color="currentColor" data-testid="filter-action-icon" />}
            onClick={toggleFilterSidebar}
          />
        </Col>

        <Sidebar
          title={<IntlMessages id="dictionaries-filter-title" />}
          visible={filterOpen}
          toggleSidebar={toggleFilterSidebar}
        >
          <FormFilterDictionary
            isSubmitting={false}
            initialValues={initFilterValues}
            filterOptions={filteredSources}
            showSourceFilter={!navigationSource || !isNull(multipleSources)}
            onReset={() => {
              resetFilter({
                searchQueryParams,
                setSearchParams,
                setInitFilterValues,
                navigationSource: multipleSources ? null : navigationSource,
                multipleSources,
              });
            }}
            onSubmit={(values) => {
              applyFilter({
                values,
                searchQueryParams,
                searchFilterFields,
                setSearchParams,
                toggleFilterSidebar,
                navigationSource: multipleSources ? null : navigationSource,
              });
            }}
          />
        </Sidebar>
      </Row>

      <Row gutter={[32, 32]}>
        <Col span={24}>
          <Card className="hp-border-color-black-40 hp-card-6">
            <Table
              sticky
              loading={makeTableLoadingObject(
                isLoading || isLoadingDeleteDictionary || isLoadingUpdateDictionary,
              )}
              rowKey="id"
              scroll={{ x: 831 }}
              columns={getDictionariesTableColumns(
                handleDelete,
                handleView,
                handleChangeStatus,
                onDownload,
                navigationSource,
                multipleSources,
                urlPrefix,
              )}
              dataSource={dictionaries}
              onChange={handleChangeTableParams}
              pagination={{
                ...basePagination,
                current: pagination.page,
                pageSize: pagination.limit,
                total: dataPagination.total,
              }}
            />
          </Card>
        </Col>
      </Row>
    </>
  );
};

export default PageDictionaries;
